import React from "react";
import LegalLayout from "@/components/LegalLayout";

const CONTENT = {
  en: {
    title: "Cookie Notice",
    lastUpdated: "September 16, 2026",
    description: "How REVIVE Regenerative Medical Clinic uses cookies and similar technologies for website functionality, performance, analytics, and communications.",
    seoPath: "/cookies",
    altPath: "/es/cookies",
    intro: [
      "REVIVE Regenerative Medical Clinic (“REVIVE”) may use cookies and similar technologies on its website to provide website functionality, improve performance, understand how visitors interact with the website, and support communications and marketing activities.",
    ],
    sections: [
      { h: "What Are Cookies?", p: [
          "Cookies are small data files placed on or accessed through your device when you visit certain websites.",
          "They can help websites remember preferences, maintain functionality, measure website performance, and understand visitor activity."] },
      { h: "Types of Cookies We May Use", p: [] },
      { h: "Essential Cookies", p: ["These cookies support functions necessary for the website to operate properly, including security, navigation, and basic functionality."] },
      { h: "Preference Cookies", p: ["These cookies may remember choices such as language or other website preferences."] },
      { h: "Analytics Cookies", p: ["Analytics technologies may help us understand information such as which pages are visited, how visitors reach the website, and how visitors interact with website content."] },
      { h: "Marketing Cookies", p: ["Where implemented, marketing technologies may be used to measure advertising campaigns or provide more relevant communications and advertisements."] },
      { h: "Third-Party Technologies", p: [
          "Some website functions may be provided by third parties, such as analytics platforms, embedded maps, social-media services, appointment tools, advertising platforms, or messaging services.",
          "Those providers may use their own cookies or similar technologies according to their respective privacy policies."] },
      { h: "Managing Cookies", p: [
          "Where applicable, visitors may use our cookie controls to accept or reject optional cookies.",
          "Cookies may also be controlled through browser settings. Blocking certain cookies may affect website functionality."] },
      { h: "Consent", p: [
          "Where consent is required by applicable law, non-essential cookies will be used according to the consent choices presented to the visitor.",
          "Visitors may modify or withdraw applicable cookie choices through the available cookie settings."] },
      { h: "Changes to This Notice", p: ["REVIVE may periodically update this Cookie Notice to reflect changes in website technologies, services, or legal requirements."] },
      { h: "Contact", p: [
          "For questions regarding cookies or privacy practices, please contact:",
          "REVIVE Regenerative Medical Clinic — Tijuana, Baja California, Mexico",
          "Please use the contact information published on our official website."] },
    ],
  },
  es: {
    title: "Aviso de Cookies",
    lastUpdated: "16 de septiembre de 2026",
    description: "Cómo REVIVE Regenerative Medical Clinic utiliza cookies y tecnologías similares para la funcionalidad del sitio web, el rendimiento, la analítica y las comunicaciones.",
    seoPath: "/es/cookies",
    altPath: "/cookies",
    intro: [
      "REVIVE Regenerative Medical Clinic (“REVIVE”) puede utilizar cookies y tecnologías similares en su sitio web para proporcionar funcionalidad, mejorar el rendimiento, comprender cómo interactúan los visitantes con el sitio y apoyar las actividades de comunicación y marketing.",
    ],
    sections: [
      { h: "¿Qué Son las Cookies?", p: [
          "Las cookies son pequeños archivos de datos que se colocan en su dispositivo, o a los que se accede a través de él, cuando usted visita ciertos sitios web.",
          "Pueden ayudar a los sitios web a recordar preferencias, mantener la funcionalidad, medir el rendimiento del sitio y comprender la actividad de los visitantes."] },
      { h: "Tipos de Cookies que Podemos Utilizar", p: [] },
      { h: "Cookies Esenciales", p: ["Estas cookies soportan funciones necesarias para que el sitio web opere correctamente, incluidas la seguridad, la navegación y la funcionalidad básica."] },
      { h: "Cookies de Preferencias", p: ["Estas cookies pueden recordar elecciones como el idioma u otras preferencias del sitio web."] },
      { h: "Cookies de Analítica", p: ["Las tecnologías de analítica pueden ayudarnos a comprender información como qué páginas se visitan, cómo llegan los visitantes al sitio web y cómo interactúan con el contenido."] },
      { h: "Cookies de Marketing", p: ["Cuando se implementan, las tecnologías de marketing pueden utilizarse para medir campañas publicitarias o proporcionar comunicaciones y anuncios más relevantes."] },
      { h: "Tecnologías de Terceros", p: [
          "Algunas funciones del sitio web pueden ser proporcionadas por terceros, como plataformas de analítica, mapas integrados, servicios de redes sociales, herramientas de citas, plataformas publicitarias o servicios de mensajería.",
          "Dichos proveedores pueden utilizar sus propias cookies o tecnologías similares de acuerdo con sus respectivas políticas de privacidad."] },
      { h: "Gestión de Cookies", p: [
          "Cuando corresponda, los visitantes pueden utilizar nuestros controles de cookies para aceptar o rechazar las cookies opcionales.",
          "Las cookies también pueden controlarse a través de la configuración del navegador. Bloquear ciertas cookies puede afectar la funcionalidad del sitio web."] },
      { h: "Consentimiento", p: [
          "Cuando la ley aplicable requiera consentimiento, las cookies no esenciales se utilizarán de acuerdo con las opciones de consentimiento presentadas al visitante.",
          "Los visitantes pueden modificar o retirar sus opciones de cookies aplicables a través de la configuración de cookies disponible."] },
      { h: "Cambios a Este Aviso", p: ["REVIVE puede actualizar periódicamente este Aviso de Cookies para reflejar cambios en las tecnologías del sitio web, los servicios o los requisitos legales."] },
      { h: "Contacto", p: [
          "Para preguntas sobre cookies o prácticas de privacidad, por favor contacte a:",
          "REVIVE Regenerative Medical Clinic — Tijuana, Baja California, México",
          "Por favor utilice la información de contacto publicada en nuestro sitio web oficial."] },
    ],
  },
};

const ALTERNATES = {
  en: "https://revivestem.com.mx/cookies",
  es: "https://revivestem.com.mx/es/cookies",
  "x-default": "https://revivestem.com.mx/cookies",
};

const Cookies = ({ es = false }) => {
  const c = es ? CONTENT.es : CONTENT.en;
  return (
    <LegalLayout testid={es ? "cookies-page-es" : "cookies-page"} title={c.title} lastUpdated={c.lastUpdated}
      intro={c.intro} sections={c.sections} description={c.description}
      es={es} altPath={c.altPath} seoPath={c.seoPath} alternates={ALTERNATES} />
  );
};

export default Cookies;
